/**
 * Single source of truth for Cosmos motion tokens (class names, timings, presets).
 * CSS keyframes live in globals.css — change timings here to stay in sync with TS usage.
 */
import { legacyPathFor } from './movedPages';

export const ANIMATION_REGISTRY = {
  stagger: {
    className: 'cosmos-item-in',
    /** Bottom-up staircase: delay between consecutive items */
    stepMs: 70,
    /**
     * Games / Wallet / Activity list cascade — one timing for all three pages.
     * (Do not override duration per page.)
     */
    compactStepMs: 26,
    compactDurationMs: 220,
    maxItems: 12,
    /** Cap high enough for Games page grids so cascade stays stepped, not a clump. */
    compactMaxItems: 18,
  },
  /**
   * Skeleton L→R shine — fixed band + duration from width so Games chips and
   * Wallet bars share the same px/s ( %-based sweeps look faster on wide chips).
   */
  shimmer: {
    className: 'cosmos-skeleton__shine',
    bandPx: 128,
    /** Leisurely sweep speed (px/s) across the skeleton. */
    pxPerSec: 40,
    minDurationMs: 4500,
    /** Fallback before first measure. */
    durationMs: 6000,
  },
  fadeIn: {
    className: 'cosmos-fade-in',
  },
  popIn: {
    className: 'cosmos-pop-in',
    originTopRight: 'cosmos-pop-in',
    originTopLeft: 'cosmos-pop-in--tl',
    originBottomCenter: 'cosmos-pop-in--bc',
  },
  modal: {
    /** Enter duration — must match CSS panel/backdrop enter. */
    enterMs: 220,
    /** Keep mounted for exit animation — must match CSS panel/backdrop exit. */
    exitMs: 180,
    backdropEnter: 'cosmos-modal-backdrop-enter',
    backdropExit: 'cosmos-modal-backdrop-exit',
    panelEnter: 'dialog-panel-enter',
    panelExit: 'dialog-panel-exit',
  },
  wave: {
    className: 'game-tile-wave',
    staggerMs: 49,
    durationMs: 315,
  },
  /**
   * Periodic mirror shine — Home browse grid + `.btn-shine` (Sign in, etc.).
   * Same keyframes (`grid-shine-sweep`), period, and wall-clock `--shine-phase`.
   */
  gridShine: {
    className: 'game-tile-shine',
    /** Full cycle length — must match `--shine-period` in CSS */
    periodMs: 5000,
    /** Delay between anti-diagonals so the gleam travels TL → BR as one band */
    staggerMs: 70,
  },
  navTabsWave: {
    className: 'nav-tab-appear',
    staggerMs: 160,
    durationMs: 520,
    /** Wait for auth modal exit before stagger (ms). */
    postLoginDelayMs: 360,
  },
  marquee: {
    containerClass: 'continue-marquee',
    trackClass: 'continue-marquee__track',
    secondsPerItem: 4,
    cardWidthPx: 264,
    protoScaleBase: 1100,
  },
  countUp: {
    portfolioMs: 800,
    headerBalanceMs: 400,
  },
  balanceFlash: {
    className: 'cosmos-balance-flash',
  },
} as const;

/** Named motion features — used by route presets and `useRouteAnimation()` */
export type AnimationFeature =
  | 'home.waveGrid'
  | 'home.continueMarquee'
  /** Play page "Pay your way" strip (PaymentMethodsStrip); static when off. */
  | 'home.paymentsMarquee'
  /** Play, signed out: the animated guest hero (src/features/home/guestHero). */
  | 'home.guestHero'
  | 'list.stagger'
  | 'list.staggerCompact'
  | 'balance.countUp'
  | 'modal.enterExit'
  | 'dropdown.popIn'
  | 'content.fadeIn';

/**
 * Which motion features are active per route prefix (longest match wins in provider).
 * Add a route here instead of hardcoding classes in page components.
 */
export const ROUTE_ANIMATION_PRESETS: Record<string, readonly AnimationFeature[]> = {
  '/': ['home.waveGrid', 'home.continueMarquee', 'home.guestHero', 'home.paymentsMarquee', 'modal.enterExit', 'dropdown.popIn'],
  '/account/finance/wallet': ['list.staggerCompact', 'modal.enterExit'],
  '/account/finance/wallet/deposit': ['content.fadeIn', 'modal.enterExit'],
  '/account/wallet/payout-request': ['content.fadeIn', 'modal.enterExit'],
  '/account/wallet/deposit-checkout': ['home.waveGrid', 'modal.enterExit'],
  '/account/transactions': ['list.staggerCompact', 'modal.enterExit'],
  '/account/games': ['list.staggerCompact', 'modal.enterExit', 'dropdown.popIn'],
  '/account/settings': ['content.fadeIn', 'modal.enterExit'],
  '/account/animations': ['content.fadeIn', 'modal.enterExit'],
  '/account/messages': ['list.staggerCompact', 'modal.enterExit'],
  '/faq': ['list.staggerCompact', 'modal.enterExit'],
  '/support': ['list.stagger', 'modal.enterExit', 'dropdown.popIn'],
};

export type StaggerVariant = 'default' | 'compact';
export type StaggerDirection = 'bottomUp' | 'topDown';

/** Stagger delay for index in a list. */
export function getStaggerDelayMs(
  index: number,
  total: number,
  variant: StaggerVariant = 'default',
  direction: StaggerDirection = 'bottomUp'
): number {
  const { stepMs, compactStepMs, maxItems, compactMaxItems } = ANIMATION_REGISTRY.stagger;
  const cap = variant === 'compact' ? compactMaxItems : maxItems;
  const step = variant === 'compact' ? compactStepMs : stepMs;
  const orderIndex =
    direction === 'topDown' ? index : Math.max(0, total - 1 - index);
  return Math.min(orderIndex, cap) * step;
}

/**
 * Inline style for one stagger item. The compact duration goes through
 * `--stagger-duration` (read by `.cosmos-item-in`), not an inline
 * animation-duration, so the reduced-motion rule in globals.css can still
 * shorten it without !important.
 */
export function getStaggerStyle(
  index: number,
  total: number,
  variant: StaggerVariant = 'default',
  direction: StaggerDirection = 'bottomUp'
): { animationDelay: string; '--stagger-duration'?: string } {
  const style: { animationDelay: string; '--stagger-duration'?: string } = {
    animationDelay: `${getStaggerDelayMs(index, total, variant, direction)}ms`,
  };
  if (variant === 'compact') {
    style['--stagger-duration'] = `${ANIMATION_REGISTRY.stagger.compactDurationMs}ms`;
  }
  return style;
}

/** Wave grid tile CSS vars for `--wave-row` / `--wave-col`. */
export function getWaveTileStyle(row: number, col: number): Record<string, number> {
  return { '--wave-row': row, '--wave-col': col } as Record<string, number>;
}

/**
 * Wall-clock phase lock for mirror shine. Call at mount (or when the shining
 * set remounts) so Home tiles and header `.btn-shine` stay on one timeline:
 * `animation-delay = stagger − (now % period)`.
 */
export function getSharedShineClockStyle(
  overrides?: Record<string, string | number>
): Record<string, string | number> {
  const periodMs = ANIMATION_REGISTRY.gridShine.periodMs;
  return {
    '--shine-period': `${periodMs}ms`,
    '--shine-phase': `${performance.now() % periodMs}ms`,
    ...overrides,
  };
}

/** Marquee track duration from item count. */
export function getMarqueeDurationSec(itemCount: number): number {
  return Math.max(itemCount, 2) * ANIMATION_REGISTRY.marquee.secondsPerItem;
}

/** Marquee CSS vars for inline style on `.continue-marquee__track`. */
export function getMarqueeTrackStyle(
  itemCount: number,
  copies: number
): Record<string, string | number> {
  return {
    '--marquee-duration': `${getMarqueeDurationSec(itemCount)}s`,
    '--marquee-copies': copies,
  };
}

/** Configurable animation routes — labels for account menu settings UI. */
export const ANIMATION_ROUTE_OPTIONS = [
  { route: '/', labelKey: 'header.tabs.home', fallback: 'Home' },
  { route: '/account/games', labelKey: 'header.tabs.games', fallback: 'Games' },
  { route: '/account/finance/wallet', labelKey: 'header.tabs.wallet', fallback: 'Wallet' },
  { route: '/account/finance/wallet/deposit', labelKey: 'walletDepositFlow.dialogTitle', fallback: 'Deposit' },
  { route: '/account/wallet/payout-request', labelKey: 'walletPayout.dialogTitle', fallback: 'Payout' },
  { route: '/account/wallet/deposit-checkout', labelKey: 'routes.walletDepositCheckout', fallback: 'Deposit checkout' },
  { route: '/account/transactions', labelKey: 'header.tabs.activity', fallback: 'Activity' },
  { route: '/account/settings', labelKey: 'header.menu.accountSettings', fallback: 'Account & settings' },
  { route: '/account/messages', labelKey: 'header.animations.pages.messages', fallback: 'Messages' },
  // Keyed by the old /faq route (the player's saved choice); it is the Help page now (movedPages.ts).
  { route: '/faq', labelKey: 'header.tabs.help', fallback: 'Help' },
  { route: '/support', labelKey: 'header.menu.support', fallback: 'Support' },
] as const;

/**
 * Resolve route preset prefix from pathname (longest matching prefix). Pages that moved under
 * Money, Help and Me resolve to their old route, which keys both the preset and the player's
 * saved per-page setting.
 */
export function resolveRouteAnimationPrefix(pathname: string): string | null {
  const normalized = legacyPathFor(pathname).replace(/\/+$/, '') || '/';
  let best: string | null = null;
  for (const route of Object.keys(ROUTE_ANIMATION_PRESETS)) {
    if (normalized === route || normalized.startsWith(`${route}/`)) {
      if (!best || route.length > best.length) best = route;
    }
  }
  return best;
}

/** Resolve route preset from pathname (longest matching prefix). */
export function resolveRouteAnimationFeatures(pathname: string): AnimationFeature[] {
  const best = resolveRouteAnimationPrefix(pathname);
  return best ? [...ROUTE_ANIMATION_PRESETS[best]] : [];
}

export function routeHasAnimationFeature(
  pathname: string,
  feature: AnimationFeature
): boolean {
  return resolveRouteAnimationFeatures(pathname).includes(feature);
}
